import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import type { LabelProps } from 'semantic-ui-react';
import { Label, Popup } from 'semantic-ui-react';

import { formatCompactNumber } from '@/utils/format-number';
import { darkReaderInvertClass } from '@components/dark-mode/dark-mode-provider';
import { StatBreakdown } from '@components/stat-breakdown/stat-breakdown';

import { COLOR_BY_SEVERITY, SEVERITY_ORDER } from './severity.const';
import s from './severity-counters.scss';

export interface SeverityCountersProps {
  critical?: number;
  high?: number;
  medium?: number;
  low?: number;
  negligible?: number;
  unknown?: number;
  /**
   * When true, counters with a value of zero are hidden.
   */
  hideZero?: boolean;
  size?: LabelProps['size'];
}

/**
 * Renders small colored counters for each severity level, ordered from most to
 * least severe. Wraps the counters in a hover popup showing the full
 * breakdown with actual values.
 */
export const SeverityCounters = ({
  critical,
  high,
  medium,
  low,
  negligible,
  unknown,
  hideZero = false,
  size = 'small',
}: SeverityCountersProps) => {
  useStyles(s);

  const counts: Omit<SeverityCountersProps, 'hideZero' | 'size'> = {
    critical,
    high,
    medium,
    low,
    negligible,
    unknown,
  };

  const allSeverities = SEVERITY_ORDER.map(severity => ({
    color: COLOR_BY_SEVERITY[severity],
    count: counts[severity] ?? 0,
    severity,
  }));

  const severities = hideZero
    ? allSeverities.filter(({ count }) => count > 0)
    : allSeverities;

  if (severities.length === 0) return null;

  const allZero = allSeverities.every(({ count }) => count === 0);

  const counters = (
    <span className={s.root} role="group" aria-label="Vulnerability severities">
      {severities.map(({ severity, count, color }) => (
        <Label
          key={severity}
          className={classNames({
            [darkReaderInvertClass]: !color,
            [s.muted]: count === 0,
          })}
          aria-label={`${count} ${severity}`}
          color={color}
          content={formatCompactNumber(count)}
          size={size}
        />
      ))}
    </span>
  );

  if (allZero) return counters;

  return (
    <Popup
      className={s.popup}
      position="bottom left"
      trigger={counters}
      hoverable
    >
      <Popup.Content>
        <StatBreakdown
          items={allSeverities.map(({ color, count, severity }) => ({
            color,
            key: severity,
            value: count,
          }))}
        />
      </Popup.Content>
    </Popup>
  );
};

export default SeverityCounters;
